<template>
    <div :class="inline ? 'ai-inline-host' : 'error-msg-wrap'">
        <div v-show="!previewNode" class="form-wrapper ai-modal" :class="inline ? 'ai-modal-inline' : 'slide-up'">
            <div v-if="!inline || phase != 'input'" class="form-close">
                <button type="button" class="form-close-x" :aria-label="inline ? 'Start over' : 'Close The Forge'" :title="inline ? 'Start over' : 'Close'" @click="requestClose()">&times;</button>
            </div>

            <div class="ai-modal-header">
                <span class="ai-icon-badge">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
                </span>
                <div v-if="createMode">
                    <div class="ai-modal-title">{{ isExtend ? 'Add to a' : 'Forge a' }} {{ typeNoun }}</div>
                    <div v-if="phase == 'review'" class="ai-modal-subtitle">{{ isExtend ? 'Check the additions, then save them as a draft.' : 'Check the draft and its details, then create it.' }}</div>
                    <div v-else class="ai-modal-subtitle" :title="trainingCount ? 'The Forge follows ' + trainingCount + ' rules set by company leadership' : ''">
                        <template v-if="trainingCount">Uses your {{ trainingCount }} company rule{{ trainingCount == 1 ? '' : 's' }}. </template>Nothing goes live until you review and save it.
                    </div>
                </div>
                <div v-else>
                    <div class="ai-modal-title">{{ editOnly ? 'Edit' : mode == 'generate' ? 'Generate' : 'Improve' }} {{ typeLabel }} <span class="ai-tag">AI</span></div>
                    <div class="ai-modal-subtitle">
                        {{ editOnly ? (initialDraft && initialDraft.changesSummary && initialDraft.changesSummary.length ? 'Your ' + typeLabel.toLowerCase() + ' with the changes you asked for. Check them, ask for more, then apply.' : 'Your current edits are loaded. Tell the AI what you want changed.') : mode == 'generate' ? 'Describe it — the AI writes it' : 'A sharper version of this ' + typeLabel.toLowerCase() + ', in seconds' }}
                        <span v-if="trainingCount" class="ai-trained-pill" :title="'This AI follows ' + trainingCount + ' rules set by company leadership'">
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                            company-trained
                        </span>
                    </div>
                </div>
            </div>

            <!-- INPUT PHASE (generate mode only) -->
            <div v-if="phase == 'input'">
                <div v-if="createMode" class="ai-type-row">
                    <span class="ai-type-label">Make a</span>
                    <span class="ai-seg">
                        <span v-for="t in typeOptions" :key="t.key" class="ai-type-pill" :class="{ 'ai-type-on': selectedType == t.key }" @click="pickType(t.key)"><svg-icon :icon="t.icon" />{{ t.label }}</span>
                    </span>
                </div>
                <ai-meeting-recorder v-if="nt == 'meeting'" ref="meetingRecorder" @busy="isRecording = $event" />
                <template v-if="nt != 'meeting'">
                <div v-if="createMode" class="ai-start-row">
                    <span class="ai-type-label">Start from</span>
                    <span class="ai-seg">
                        <span class="ai-type-pill" :class="{ 'ai-type-on': startFrom == 'new' }" @click="setStartFrom('new')"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M12 11v6M9 14h6"/></svg>New {{ typeNoun }}</span>
                        <span class="ai-type-pill" :class="{ 'ai-type-on': startFrom == 'existing' }" @click="setStartFrom('existing')"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h4"/></svg>Add to an existing one</span>
                    </span>
                </div>
                <div v-if="createMode && startFrom == 'existing'" class="ai-target">
                    <div v-if="targetSlug" class="ai-target-chip">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                        Adding to <strong>{{ targetName }}</strong>
                        <a @click.prevent="clearTarget()" title="Choose a different one">change</a>
                    </div>
                    <template v-else>
                        <input type="text" class="ai-target-search" v-model="nodeSearch" :placeholder="'Search your ' + typeLabel.toLowerCase() + 's...'" autofocus />
                        <div class="ai-target-list">
                            <div v-if="loadingNodes" class="ai-muted" style="padding:8px 10px;font-size:13px">Loading...</div>
                            <div v-else-if="!filteredNodes.length" class="ai-muted" style="padding:8px 10px;font-size:13px">No {{ typeLabel.toLowerCase() }}s match</div>
                            <div v-for="n in filteredNodes" :key="n.slug" class="ai-target-option" @click="pickTarget(n)">
                                {{ n.name }} <span v-if="n.draft" class="ai-target-draft">draft</span>
                            </div>
                        </div>
                    </template>
                </div>
                <p v-if="isExtend" class="ai-muted">Describe what to add<span v-if="nt == 'sheet'"> — a whole new role, or more duties for a role that's already there</span><span v-else-if="nt == 'checklist'"> — new tasks or a new section</span><span v-else-if="nt == 'timeline'"> — new steps in the flow</span><span v-else> — new steps or sections</span>. You can also upload documents or photos (PDF, Word, text) that describe the additions. Everything already there stays exactly as it is; the AI writes the additions in the same style.</p>
                <p v-else-if="nt == 'process' && videoImprove" class="ai-muted"><strong>Show the AI how this process is done now</strong> — record your screen, upload a recording, add photos of the work, or upload the current written procedure (PDF, Word, text). The AI compares it against the current version of this process and drafts the update.</p>
                <p v-else-if="!createMode && nt == 'process' && canRecord" class="ai-muted">Describe the process in your own words, <strong>record your screen right here</strong> while you do it, upload a screen recording, add photos of hands-on work, or upload existing documents (PDF, Word, text) — the AI writes the process, and attaches an image to each step it saw. You can also add a note alongside a capture.</p>
                <p v-else-if="!createMode && nt == 'process'" class="ai-muted">Describe the process in your own words, <strong>upload a screen recording, photos or documents</strong> (PDF, Word, text) — the AI writes the process and attaches an image to each step it saw. (To record your screen directly in the app, open this page on a desktop computer.)</p>
                <p v-else-if="!createMode && nt == 'checklist'" class="ai-muted">Describe the checklist in your own words — what job it's for and what needs to be checked — or upload a document or photos to build it from. The AI matches the format of your existing checklists.</p>
                <p v-else-if="!createMode && nt == 'sheet'" class="ai-muted">Describe the sheet you need — for example a role and its duties — or upload a job description, org chart or notes to build it from. The AI matches the sections and style of your existing role sheets.</p>
                <p v-else-if="!createMode && nt == 'timeline'" class="ai-muted">Describe the workflow — the steps, who handles each one, and rough timing if you know it — or upload a document or diagram of it. The AI matches the format of your existing value engines.</p>
                <div v-if="handoffSent" class="ai-handoff-done">
                    <strong>Sent to {{ handoffSent.to }}.</strong> They get an email with the recording, and it waits under My AI Captures for you both until they build or discard it.
                    <a @click.prevent="handoffSent = null">OK</a>
                </div>
                <div v-if="createMode" class="ai-methods" :class="{ 'ai-methods-locked': isRecording }">
                    <button type="button" class="ai-method" :class="{ 'ai-method-on': inputMethod == 'describe' }" @click="setMethod('describe')">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h5"/></svg>
                        <strong>Describe it</strong><span>Type it, or tap the mic and talk.</span>
                    </button>
                    <button v-if="recordable && canRecord" type="button" class="ai-method" :class="{ 'ai-method-on': inputMethod == 'record' }" @click="setMethod('record')">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                        <strong>Record your screen</strong><span>Do the work while the Forge watches and listens.</span>
                    </button>
                    <button type="button" class="ai-method" :class="{ 'ai-method-on': inputMethod == 'upload' }" @click="setMethod('upload')">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                        <strong>Upload</strong><span>{{ recordable ? 'Recordings, photos, PDFs, Word or text files.' : 'Photos, PDFs, Word or text files.' }}</span>
                    </button>
                </div>
                <p v-if="createMode && recordable && !canRecord" class="ai-muted" style="font-size:12.5px;margin:-4px 0 12px">To record your screen right here, open this page on a desktop computer.</p>
                <div class="ai-input-stack" :class="{ 'ai-capture-first': createMode && inputMethod != 'describe' }">
                <div class="ai-textarea-wrap">
                    <textarea v-model="description" rows="5" :placeholder="inputPlaceholder" style="width:100%;box-sizing:border-box"></textarea>
                    <ai-dictation class="ai-dict-pos" @text="description = (description ? description.trim() + ' ' : '') + $event" />
                </div>
                <div class="ai-video-row">
                    <ai-screen-recorder v-if="recordable && !videoFile && !screenRec" v-show="!createMode || inputMethod == 'record'" @recorded="onScreenRecorded" @recording="isRecording = $event" @discard="discardScreenRec()" />
                    <div v-if="recordable && screenRec" class="ai-rec-preview">
                        <video :src="screenRecUrl" controls playsinline></video>
                        <div class="ai-rec-preview-row">
                            <span class="ai-rec-caption">Screen recording — {{ screenRecLabel }}</span>
                            <a @click.prevent="discardScreenRec()">Remove</a>
                        </div>
                    </div>
                    <label v-if="recordable && !screenRec && !isRecording && (!createMode || inputMethod == 'upload')" class="ai-btn ai-btn-ghost" style="cursor:pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15 8v8H5V8h10zm2 2.5V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4.5l5 4v-11l-5 4z"/></svg>
                        {{ videoFile ? 'Change recording' : 'Upload screen recording' }}
                        <input type="file" accept="video/mp4,video/quicktime,video/webm" style="display:none" @change="onVideoPicked" />
                    </label>
                    <span v-if="recordable && videoFile" class="ai-video-chip">
                        {{ videoFile.name }} ({{ (videoFile.size / 1024 / 1024).toFixed(1) }} MB)
                        <a @click.prevent="videoFile = null" style="cursor:pointer;margin-left:6px">&times;</a>
                    </span>
                    <label v-if="!videoFile && !screenRec && !isRecording && (!createMode || inputMethod == 'upload')" class="ai-btn ai-btn-ghost" style="cursor:pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 10.5a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0-2a5 5 0 1 1 0 10 5 5 0 0 1 0-10zM20 5h-3.17l-1.24-1.35A2 2 0 0 0 14.12 3H9.88a2 2 0 0 0-1.47.65L7.17 5H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"/></svg>
                        {{ (photos.length || docs.length) ? 'Add more files' : 'Add photos or documents' }}
                        <input type="file" :accept="fileAccept" multiple style="display:none" @change="onFilesPicked" />
                    </label>
                </div>
                <!-- Keelan, 27 Oct 2026: keep the recording as a Video beside what it builds, or only use it to write the draft -->
                <label v-if="recordable && (screenRec || videoFile) && !isRecording" class="ai-save-video">
                    <input type="checkbox" v-model="saveVideo" />
                    <span class="ai-save-video-switch" aria-hidden="true"></span>
                    <span><strong>Save the video with it</strong><em>{{ saveVideo ? 'The recording is added to Videos next to the new ' + typeNoun + '.' : 'The recording is only used to write the draft, then deleted.' }}</em></span>
                </label>
                <div v-if="photos.length || docs.length" class="ai-photo-row">
                    <div v-for="(photo, i) in photos" :key="'p' + i" class="ai-photo-thumb">
                        <img :src="photo.url" />
                        <span class="ai-photo-x" @click="removePhoto(i)">&times;</span>
                    </div>
                    <span v-for="(doc, i) in docs" :key="'d' + i" class="ai-doc-chip" :title="doc.file.name">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8 13h8v2H8v-2zm0 4h8v2H8v-2z"/></svg>
                        <span class="ai-doc-name">{{ doc.file.name }}</span>
                        <em>{{ doc.file.size >= 1024 * 1024 ? (doc.file.size / 1024 / 1024).toFixed(1) + ' MB' : Math.max(1, Math.round(doc.file.size / 1024)) + ' KB' }}</em>
                        <a @click.prevent="removeDoc(i)">&times;</a>
                    </span>
                    <span class="ai-muted" style="font-size:12px;align-self:center">
                        <template v-if="photos.length">{{ photos.length }}/15 photos — order matters, arrange the story of the task</template>
                        <template v-if="photos.length && docs.length"> &middot; </template>
                        <template v-if="docs.length">{{ docs.length }} document{{ docs.length == 1 ? '' : 's' }} — the AI reads {{ docs.length == 1 ? 'it' : 'them' }} in full</template>
                    </span>
                </div>
                </div>
                <!-- Hand the recording to another editor instead of building it now (AIRecordingHandoff.vue) -->
                <ai-recording-handoff v-if="canHandoff" :recording="screenRec" :file="videoFile" :note="description" :node-type="nt" @sent="onHandoffSent" />
                <div style="display:flex;justify-content:flex-end;margin-top:12px">
                    <div class="ai-btn" :class="(isRecording || (createMode && startFrom == 'existing' && !targetSlug) || (videoImprove ? !hasCapture : (!description.trim() && !hasCapture))) ? 'disable-button' : ''" @click="generate()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
                        {{ isExtend ? 'Write the Additions' : 'Generate ' + typeLabel }}
                    </div>
                </div>
                </template>
            </div>

            <!-- LOADING PHASE -->
            <div v-if="phase == 'loading'" class="ai-loading">
                <div class="ai-loading-orb"></div>
                <template v-if="uploadPct !== null">
                    <div class="ai-upload-bar"><span :style="{ width: uploadPct + '%' }"></span></div>
                    <div class="ai-loading-msg">Uploading recording... {{ uploadPct }}%</div>
                </template>
                <div v-else class="ai-loading-msg">{{ (activeJobId && jobStage) || loadingMessage }}</div>
                <div class="ai-muted" style="font-size:13px">This usually takes 20–40 seconds</div>
                <div v-if="activeJobId" class="ai-muted" style="font-size:13px;margin-top:10px"><strong>{{ inline ? 'You can leave this page.' : 'You can close this window.' }}</strong> If you're away when it finishes, we'll email you a link. If you review the result here, no email is needed.</div>
            </div>

            <!-- ERROR -->
            <div v-if="error" class="ai-error">
                {{ error }}
                <a @click.prevent="retry()" style="margin-left:8px">Try again</a>
            </div>

            <!-- REVIEW PHASE -->
            <div v-if="phase == 'review' && draft">
                <div v-if="isExtend" class="ai-draft-name" style="cursor:default"><span class="ai-extend-lead">Adding to</span> {{ draft.name }}</div>
                <input v-else-if="editingName" class="ai-edit-name" v-model="draft.name" @keyup.enter="editingName = false" @blur="editingName = false" />
                <div v-else class="ai-draft-name" title="Click to edit the name" @click="editingName = true">{{ draft.name }}<span class="ai-edit-btn"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M14.078 4.232l5.688 5.688-9.398 9.398-5.687-5.688 9.397-9.398zm5.586-1.727l1.831 1.831c.78.781.78 2.047 0 2.828l-1.485 1.485-5.687-5.688 1.484-1.484c.781-.781 2.048-.781 2.828 0l1.029 1.028zm-17.664 17.667l1.831-6.518 5.688 5.687-6.518 1.832c-.554.155-1.156-.447-1.001-1.001z"/></svg></span></div>

                <div v-if="draft.changesSummary && draft.changesSummary.length" class="ai-changes">
                    <div class="ai-section-label">{{ revisionCount > 0 ? 'What changed in this revision' : 'What the AI did' }}</div>
                    <ul>
                        <li v-for="(change, i) in draft.changesSummary" :key="i">{{ change }}</li>
                    </ul>
                </div>

                <div v-if="placeholderCount" class="ai-placeholder-warn">
                    {{ placeholderCount }} detail{{ placeholderCount == 1 ? '' : 's' }} marked <strong>[confirm: ...]</strong> for you to verify — fill these in before publishing.
                </div>
                <div class="ai-section-label">{{ typeLabel }} preview</div>
                <!-- Role sheet: one card per seat (when adding to a sheet, only the new / changed seats) -->
                <div v-if="nt == 'sheet' && draft.sheet" class="ai-preview ai-preview-seats">
                    <div v-for="seat in previewSeats" :key="seat.id" :class="['ai-seat', { 'ai-new': isNewSeat(seat) }]">
                        <div class="ai-seat-who">{{ seatLabel(seat) || 'New seat' }}<span v-if="isNewSeat(seat)" class="ai-seat-tag">New</span></div>
                        <div v-if="seat.accountabilities.length" :class="['ai-seat-sec', { 'ai-new-cell': isChangedSection(seat, 'acc') }]">
                            <div class="ai-seat-h">Accountabilities</div>
                            <ol><li v-for="(a, k) in seat.accountabilities" :key="k"><b>{{ a.value }}</b><span v-if="a.value2"> — {{ a.value2 }}</span></li></ol>
                        </div>
                        <template v-for="section in draft.sheet.sections" :key="section.id">
                            <div v-if="seat.values[section.id]" :class="['ai-seat-sec', { 'ai-new-cell': isChangedSection(seat, section.id) }]">
                                <div class="ai-seat-h">{{ section.label }}</div>
                                <div v-html="renderBody(seat.values[section.id])"></div>
                            </div>
                        </template>
                    </div>
                    <div v-if="isExtend && draft.sheet.seats.length > previewSeats.length" class="ai-muted" style="font-size:13px">{{ draft.sheet.seats.length - previewSeats.length }} other seat{{ draft.sheet.seats.length - previewSeats.length == 1 ? '' : 's' }} unchanged.</div>
                </div>
                <div v-if="nt == 'sheet'" class="ai-muted" style="font-size:13px;margin-top:8px">People are linked to a seat from their employee page (Assign Role) after saving.</div>
                <div v-if="nt == 'timeline'" class="ai-preview">
                    <div v-for="(item, i) in draft.items" :key="i" class="ai-preview-tl" :class="{ 'ai-new': isNewItem(i) }">
                        <span class="ai-tl-rail"><span class="ai-tl-dot" :style="{ background: item.color }"></span></span>
                        <div class="ai-tl-body">
                            <div class="ai-tl-line1"><span v-html="renderText(item.lineOne)"></span><span v-if="isNewItem(i)" class="ai-new-tag">New</span></div>
                            <div v-if="item.lineTwo" class="ai-tl-line2" v-html="renderText(item.lineTwo)"></div>
                            <div v-if="item.lineThree" class="ai-tl-line2" v-html="renderText(item.lineThree)"></div>
                            <div v-if="smallLines(item)" class="ai-tl-small" v-html="renderText(smallLines(item))"></div>
                            <div v-if="item.description" class="ai-tl-desc" v-html="renderBody(item.description)"></div>
                        </div>
                    </div>
                </div>
                <div v-if="nt == 'timeline'" class="ai-muted" style="font-size:13px;margin-top:8px">Step links and images are added by you after saving — colors follow your existing value engines.</div>
                <div v-if="nt == 'checklist'" class="ai-preview">
                    <div v-for="(item, i) in draft.items" :key="i" :class="{ 'ai-new': isNewChecklistItem(item) }">
                        <div v-if="item.type == 'textblock'" class="ai-preview-textblock"><span v-html="renderBody(item.text)"></span><span v-if="isNewChecklistItem(item)" class="ai-new-tag">New</span></div>
                        <div v-else class="ai-preview-task">
                            <span class="ai-task-box"></span>
                            <div class="ai-task-text">
                                <strong v-html="renderText(item.label)"></strong><span v-if="item.description" class="ai-task-desc">: <span v-html="renderText(item.description)"></span></span><span v-if="isNewChecklistItem(item)" class="ai-new-tag">New</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="nt == 'process'" class="ai-preview">
                    <div v-for="(item, i) in draft.items" :key="i" class="ai-preview-item" :class="{ 'ai-new': isNewItem(i) }">
                        <template v-if="editingIdx === i">
                            <input class="ai-edit-header" v-model="item.header" />
                            <tip-tap v-model="item.body" :show-align="true" />
                            <div style="display:flex;justify-content:flex-end;margin-top:8px">
                                <div class="ai-btn ai-btn-ghost ai-edit-done" @click="editingIdx = null">Done</div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="ai-preview-header">
                                {{ item.header }}<span v-if="isNewItem(i)" class="ai-new-tag">New</span>
                                <span v-if="!isExtend || isNewItem(i)" class="ai-step-tools">
                                    <span class="ai-edit-btn" title="Edit this step" @click="editingIdx = i">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M14.078 4.232l5.688 5.688-9.398 9.398-5.687-5.688 9.397-9.398zm5.586-1.727l1.831 1.831c.78.781.78 2.047 0 2.828l-1.485 1.485-5.687-5.688 1.484-1.484c.781-.781 2.048-.781 2.828 0l1.029 1.028zm-17.664 17.667l1.831-6.518 5.688 5.687-6.518 1.832c-.554.155-1.156-.447-1.001-1.001z"/></svg>
                                    </span>
                                    <span v-if="!isExtend" class="ai-edit-btn" :class="{ 'ai-tool-off': i === 0 }" title="Move up" @click="moveStep(i, -1)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l12 18h-24z"/></svg>
                                    </span>
                                    <span v-if="!isExtend" class="ai-edit-btn" :class="{ 'ai-tool-off': i === draft.items.length - 1 }" title="Move down" @click="moveStep(i, 1)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21l-12-18h24z"/></svg>
                                    </span>
                                    <span v-if="!isExtend" class="ai-edit-btn ai-tool-danger" title="Delete this step" @click="deleteStep(i)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
                                    </span>
                                </span>
                            </div>
                            <div class="ai-preview-body" v-html="renderBody(item.body)"></div>
                        </template>
                        <img v-if="item.image" :src="draftMediaUrl(item.image)" class="ai-preview-shot" loading="lazy" title="Click to enlarge" @click="lightboxUrl = draftMediaUrl(item.image)" />
                    </div>
                </div>
                <div v-if="nt == 'process' && !isExtend" style="margin-top:8px">
                    <a class="ai-add-step" @click.prevent="addStep()">+ Add a step</a>
                </div>

                <!-- CREATE MODE: the details the manual Add form used to ask for up front,
                     pre-filled from the AI's suggestions and editable in place -->
                <div v-if="createMode && meta && !isExtend" class="ai-details">
                    <div class="ai-section-label" style="margin-top:0">Details <span class="ai-details-hint">filled in by the AI — change anything you like</span></div>
                    <div class="ai-details-grid">
                        <div v-if="nt != 'checklist'" class="ai-details-field ai-details-wide">
                            <label>{{ typeLabel }} owner</label>
                            <process-owner-picker v-model="meta.processOwnerIDs" />
                        </div>
                        <div class="ai-details-field">
                            <label>Locations</label>
                            <div class="ai-pills">
                                <span v-for="(loc, i) in companyLocations" :key="i" class="ai-pill" :class="{ 'ai-pill-on': meta.locations.includes(loc) }" @click="togglePick(meta.locations, loc)">{{ loc }}</span>
                            </div>
                            <div v-if="!meta.locations.length" class="ai-field-warn">Pick at least one location</div>
                        </div>
                        <div class="ai-details-field">
                            <label>Departments</label>
                            <div class="ai-pills">
                                <span v-for="(dep, i) in companyDepartments" :key="i" class="ai-pill" :class="{ 'ai-pill-on': meta.departments.includes(dep) }" @click="togglePick(meta.departments, dep)">{{ dep }}</span>
                            </div>
                            <div v-if="!meta.departments.length" class="ai-field-warn">Pick at least one department</div>
                        </div>
                        <div v-if="companyTypes.length" class="ai-details-field ai-details-wide">
                            <label>Types</label>
                            <div class="ai-pills">
                                <span v-for="(t, i) in companyTypes" :key="i" class="ai-pill ai-pill-alt" :class="{ 'ai-pill-on': meta.types.includes(t) }" @click="togglePick(meta.types, t)">{{ t }}</span>
                            </div>
                        </div>
                        <div class="ai-details-field ai-details-wide">
                            <label>Folders</label>
                            <folder-picker v-model="meta.folderSlugs" />
                            <div v-if="!meta.folderSlugs.length" class="ai-field-warn">Pick at least one folder so people can find it</div>
                        </div>
                        <div class="ai-details-field ai-details-wide">
                            <label>Link</label>
                            <div class="ai-slug-row"><span>/playbook/</span><input type="text" v-model="meta.slug" @input="onSlugEdit" /></div>
                        </div>
                    </div>
                    <div v-if="detailsError" class="ai-error" style="margin:10px 0 0">{{ detailsError }}</div>
                </div>
                <div v-else-if="draft.suggestedDepartments?.length || draft.suggestedTypes?.length" style="margin:14px 0">
                    <span v-for="(d, i) in draft.suggestedDepartments" :key="'d'+i" class="ai-chip">{{ d }}</span>
                    <span v-for="(t, i) in draft.suggestedTypes" :key="'t'+i" class="ai-chip ai-chip-type">{{ t }}</span>
                </div>
                <div v-if="recordingBecomesVideo" class="ai-video-note">
                    <label for="ai-video-title">&#9654; <strong>Video title</strong></label>
                    <input id="ai-video-title" type="text" maxlength="200" :value="videoTitleValue" @input="videoTitle = $event.target.value; videoTitleEdited = true" :placeholder="videoDefaultTitle || 'Name the video'" />
                    <span>The screen recording is saved under <strong>Processes &rarr; Videos</strong> as &ldquo;{{ videoNodeName }}&rdquo;.</span>
                </div>

                <div class="ai-section-label">Want anything changed?</div>
                <div class="ai-textarea-wrap">
                    <textarea v-model="feedback" rows="3" placeholder="e.g. Add a step for documenting the claim in the CRM, and make the safety warnings stronger..." style="width:100%;box-sizing:border-box"></textarea>
                    <ai-dictation class="ai-dict-pos" @text="feedback = (feedback ? feedback.trim() + ' ' : '') + $event" />
                </div>

                <div class="ai-actions">
                    <button type="button" class="ai-btn" :disabled="!feedback.trim() || busy" :class="!feedback.trim() || busy ? 'disable-button' : ''" @click="requestChanges()">
                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m16 3 5 5M3 21l5-1L21 7a2 2 0 0 0-4-4L4 16z" /></svg>
                        Request Changes
                    </button>
                    <button v-if="nt == 'process'" ref="previewButton" type="button" class="ai-btn ai-btn-ghost" :disabled="previewLoading || busy" @click="openDraftPreview()">{{ previewLoading ? 'Opening Preview…' : 'Preview' }}</button>
                    <button type="button" class="ai-btn ai-btn-ghost ai-accept" :disabled="busy" :class="busy ? 'disable-button' : ''" @click="accept()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                        {{ busy ? (isExtend ? 'Saving...' : 'Creating...') : (isExtend ? 'Save Additions as Draft' : acceptLabel || (createMode ? 'Create ' + typeLabel : mode == 'generate' ? 'Use This ' + typeLabel : 'Apply to Editor')) }}
                    </button>
                </div>
            </div>
        </div>
        <div v-if="lightboxUrl" class="ai-lightbox" @click="lightboxUrl = ''">
            <img :src="lightboxUrl" />
        </div>
        <teleport to="body">
            <div v-if="previewNode" ref="previewDialog" tabindex="-1" class="playbook-preview-overlay ai-draft-preview" role="dialog" aria-modal="true" aria-label="Process preview" @keydown.esc.stop="closeDraftPreview()">
                <playbook-view :preview-node="previewNode" @close-preview="closeDraftPreview()" />
            </div>
        </teleport>
    </div>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue';
import axios from 'axios';
import AIDictation from '@/components/AIDictation.vue';
import AIScreenRecorder from '@/components/AIScreenRecorder.vue';
import AIRecordingHandoff from '@/components/AIRecordingHandoff.vue';
import AIMeetingRecorder from '@/components/AIMeetingRecorder.vue';
import TipTap from '@/components/TipTap.vue';
import ProcessOwnerPicker from '@/components/ProcessOwnerPicker.vue';
import FolderPicker from '@/components/FolderPicker.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { metaFromDraft, slugify } from '@/ai/createNodeFromDraft';
import { processDraftItems } from '@/ai/processDraft';
import { seatLabel } from '@/roleSheet';

// Document uploads (PDF / Word / text) — photos use image/* alongside these
const DOC_ACCEPT = '.pdf,.docx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv';
const DOC_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
const MAX_DOCS = 10;

const LOADING_MESSAGES: any = {
    extend: [
        "Reading what's already there...",
        'Working out what to add and where it fits...',
        'Writing the additions in the same style...',
        'Fitting them in...'
    ],
    documents: [
        'Uploading your documents...',
        'Reading the documents...',
        'Pulling out the steps, roles and requirements...',
        'Reorganizing everything into a clear process...',
        'Writing the process...'
    ],
    generate: [
        'Reading your description...',
        'Identifying the steps and who owns them...',
        'Writing clear, actionable instructions...',
        'Adding checks, criteria and safety call-outs...',
        'Structuring the final process...'
    ],
    improve: [
        'Reading the current process...',
        'Looking for gaps, vague steps and missing ownership...',
        'Rewriting for clarity and completeness...',
        'Adding checks, criteria and safety call-outs...',
        'Finalizing the improved version...'
    ],
    revise: [
        'Reading your feedback...',
        'Revising the draft...',
        'Polishing the updated process...'
    ],
    video: [
        'Uploading your recording...',
        'Watching the screen recording...',
        'Reading the on-screen actions and text...',
        'Reconstructing the workflow step by step...',
        'Writing the process...'
    ],
    'generate-checklist': [
        'Reading your description...',
        'Checking your existing checklists for format...',
        'Breaking the job into checkable steps...',
        'Ordering tasks the way the work is done...',
        'Structuring the final checklist...'
    ],
    'improve-checklist': [
        'Reading the current checklist...',
        'Looking for vague or compound items...',
        'Making every task concrete and checkable...',
        'Adding missing checks and criteria...',
        'Finalizing the improved checklist...'
    ],
    'generate-sheet': [
        'Reading your description...',
        'Studying your existing sheets for format...',
        'Laying out the seats and sections...',
        'Writing the duties for each column...',
        'Structuring the final sheet...'
    ],
    'generate-timeline': [
        'Reading your description...',
        'Studying your existing value engines for format...',
        'Mapping the workflow into steps and phases...',
        'Adding timing and ownership lines...',
        'Structuring the final value engine...'
    ],
    'improve-timeline': [
        'Reading the current value engine...',
        'Looking for missing hand-offs and vague steps...',
        'Tightening timing and ownership lines...',
        'Finalizing the improved value engine...'
    ]
};

export default defineComponent({
    name: 'AIProcessModal',
    // mode: 'generate' | 'improve'; nodeType: 'process' (default) | 'checklist' | 'sheet' | 'timeline';
    // acceptLabel overrides the accept button text; initialDraft/jobId/initialNote open the modal
    // directly in review phase (resume-review flow); createMode = the quick-create flow: a type
    // picker up front, and the review step collects the node details (owner, locations,
    // departments, types, link) pre-filled from the AI so the host can create the node directly —
    // accept then emits (draft, jobId, meta). busy disables the accept button while the host creates.
    // inline = rendered as a card on the page (The Forge) instead of a modal;
    // fromSlug = the folder create mode was opened from, preselected in the details' folder picker
    props: ['mode', 'slug', 'acceptLabel', 'nodeType', 'initialDraft', 'jobId', 'initialNote', 'videoImprove', 'createMode', 'busy', 'editOnly', 'previewBaseNode', 'inline', 'fromSlug', 'initialMethod', 'currentDraft'],
    components: { PlaybookView: defineAsyncComponent(async (): Promise<any> => (await import('@/views/Playbook.vue')).default), AiMeetingRecorder: AIMeetingRecorder, AiDictation: AIDictation, AiScreenRecorder: AIScreenRecorder, AiRecordingHandoff: AIRecordingHandoff, TipTap, ProcessOwnerPicker, FolderPicker, SvgIcon },
    emits: ['close', 'accept'],
    data() {
        return {
            selectedType: (this as any).nodeType || 'process', // create mode: chosen in the type picker
            typeOptions: [
                { key: 'process', label: 'Process', icon: 'papers' },
                { key: 'checklist', label: 'Powerlist', icon: 'checklist' },
                { key: 'sheet', label: 'Role sheet', icon: 'table' },
                { key: 'timeline', label: 'Value engine', icon: 'timeline' }
            ],
            inputMethod: 'describe',  // create mode: which method card is open — 'describe' | 'record' | 'upload'
            meta: null as any,        // create mode: DraftMeta being edited in the review step
            detailsError: '',
            slugTouched: false,       // stop auto-slugging once the user edits the link by hand
            docs: [] as any[],        // { file } — PDF / Word / text uploads, sent alongside photos
            startFrom: 'new',         // create mode: 'new' | 'existing' (add to an existing node)
            targetSlug: '',
            targetName: '',
            nodeSearch: '',
            nodeOptions: [] as any[], // { slug, name, draft } for the selected type
            loadingNodes: false,
            phase: 'input',
            description: '',
            feedback: '',
            draft: null as any,
            previewNode: null as any,
            previewLoading: false,
            displayedCompletion: null as any,
            acknowledgingCompletion: false,
            acknowledgedCompletion: '',
            completionAttemptKey: '',
            completionAttempts: 0,
            completionRetryTimer: null as any,
            componentGone: false,
            error: null as any,
            revisionCount: 0,
            loadingMessage: '',
            loadingTimer: null as any,
            trainingCount: 0,
            videoFile: null as any,
            screenRec: null as any, // { blob, durationSec, mimeType } from AIScreenRecorder
            screenRecUrl: '', // object URL for the parent-owned preview (survives loading-phase remounts)
            isRecording: false,
            editingIdx: null as any,
            editingName: false,
            activeJobId: null as any,
            pollTimer: null as any,
            pollFailures: 0,
            jobStage: '',
            lightboxUrl: '',
            photos: [] as any[], // { blob, url } — client-resized JPEGs, in order
            lastJobId: null as any, // survives stopJobPolling so accept() can report it
            uploadPct: null as any,
            handoffSent: null as any, // { jobId, to } after a recording was sent to someone else to build
            saveVideo: true,          // keep a recording as a Video node (off = only used to write the draft)
            videoTitle: '',           // that Video node's title; follows the draft's name until edited
            videoTitleEdited: false
        }
    },
    computed: {
        nt(): string {
            return this.createMode ? this.selectedType : (this.nodeType || 'process');
        },
        // The draft carries a "watch the original" link only when a capture job stored
        // its recording — that recording becomes a Video node when the draft is accepted
        recordingBecomesVideo(): boolean {
            if (this.isExtend || !this.draft) return false;
            if (this.nt !== 'process') return !!this.draft.recordingSaved; // set by the job when it kept the recording
            if (!this.draft.items) return false;
            return this.draft.items.some((item: any) => /\/api\/appfile\/[^"]*ai_recording_/.test(item.body || ''));
        },
        videoDefaultTitle(): string {
            return String((this.createMode && this.meta && this.meta.name) || (this.draft && this.draft.name) || '').trim();
        },
        videoTitleValue(): string {
            return this.videoTitleEdited ? this.videoTitle : this.videoDefaultTitle;
        },
        // Matches the server's forgeVideoName()
        videoNodeName(): string {
            const base = (this.videoTitleValue.trim() || this.videoDefaultTitle).replace(/\s*\((video|built by the forge)\)$/i, '').trim();
            return base + ' (Built by the Forge)';
        },
        typeLabel(): string {
            return this.nt == 'meeting' ? 'Meeting notes' : this.nt == 'checklist' ? 'Powerlist' : this.nt == 'sheet' ? 'Role Sheet' : this.nt == 'timeline' ? 'Value Engine' : 'Process';
        },
        typeNoun(): string {
            return this.typeLabel.toLowerCase();
        },
        hasCapture(): boolean {
            return !!(this.videoFile || this.screenRec || this.photos.length || this.docs.length);
        },
        // Add-to-existing: chosen up front in create mode, or implied by a draft
        // that came back from an extend job (resume-review page)
        isExtend(): boolean {
            return !!((this.draft && this.draft.extend) || (this.createMode && this.startFrom == 'existing'));
        },
        filteredNodes(): any[] {
            const q = this.nodeSearch.trim().toLowerCase();
            const list = q ? this.nodeOptions.filter((n: any) => (n.name || '').toLowerCase().includes(q) || (n.slug || '').includes(q)) : this.nodeOptions;
            return list.slice(0, 40);
        },
        fileAccept(): string {
            return 'image/*,' + DOC_ACCEPT;
        },
        companyLocations(): string[] { return (this.$store.state.user.locations || []).slice(); },
        companyDepartments(): string[] { return (this.$store.state.user.departments || []).slice(); },
        companyTypes(): string[] { return (this.$store.state.user.nodeTypes || []).slice(); },
        inputPlaceholder(): string {
            if (this.videoFile || this.screenRec) return 'Optional: anything the AI should know about this recording...';
            if (this.isExtend && (this.docs.length || this.photos.length)) return 'Optional: what from these files should be added, and where...';
            if (this.isExtend && this.nt == 'sheet') return 'e.g. Add a Maintenance Manager role with duties for preventive maintenance, repairs, parts inventory and contractor coordination — or: add weekly forklift inspections to the Plant Manager\'s key responsibilities...';
            if (this.isExtend && this.nt == 'checklist') return 'e.g. Add a section for end-of-shift cleanup with tasks for wiping down the wrapper, sweeping the staging lanes and logging film usage...';
            if (this.isExtend && this.nt == 'timeline') return 'e.g. Add a credit-check step between order entry and production scheduling, owned by accounting...';
            if (this.isExtend) return 'e.g. Add a step for photographing the staged pallet before the truck is booked, after the labelling step...';
            if (this.docs.length && !this.photos.length) return 'Optional: which process to focus on, or anything the AI should know about these documents...';
            if (this.docs.length) return 'Optional: anything the AI should know about these photos and documents...';
            if (this.nt == 'checklist') return 'e.g. A pre-delivery checklist for loading a truck: confirm the order against the packing slip, inspect each pallet for damage, strap and wrap the load, photograph it, and sign off with the shipping manager...';
            if (this.nt == 'sheet') return 'e.g. A role sheet for a Maintenance Manager — same format as our other role sheets, with the duties for preventive maintenance, repairs, parts inventory and contractor coordination...';
            if (this.nt == 'timeline') return 'e.g. Our warranty claim workflow: claim comes in by phone or email, CS logs it and checks coverage, production inspects the product photos, we approve or deny, then ship the replacement and follow up...';
            return 'e.g. When a customer calls with a warranty claim we get their info and order number, check if the product is still under warranty, and either send a replacement or explain why it isn\'t covered...';
        },
        canRecord(): boolean {
            return !!(navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia);
        },
        // In the Forge (create mode) a screen recording can build any new process, powerlist, role
        // sheet or value engine (Keelan, 27 Oct 2026); adding to an existing one from a recording,
        // and the editors' own AI buttons, stay process-only.
        recordable(): boolean {
            return this.nt == 'process' || (!!this.createMode && ['checklist', 'sheet', 'timeline'].includes(this.nt) && !this.isExtend);
        },
        // A new recording (captured or uploaded) can be handed to another editor instead
        canHandoff(): boolean {
            return !!this.createMode && this.recordable && !this.isExtend && !this.videoImprove && !this.isRecording && this.phase == 'input' && !!(this.screenRec || this.videoFile);
        },
        screenRecLabel(): string {
            if (!this.screenRec) return '';
            const s = Math.round(this.screenRec.durationSec);
            return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}, ${(this.screenRec.blob.size / 1024 / 1024).toFixed(1)} MB`;
        },
        // Role sheet seats to show: all of them, or when adding to a sheet just the new / changed ones
        previewSeats(): any[] {
            const seats = (this.draft && this.draft.sheet && this.draft.sheet.seats) || [];
            if (!this.isExtend) return seats;
            const changed = (this.draft.added && this.draft.added.sections) || [];
            return seats.filter((seat: any) => this.isNewSeat(seat) || changed.some((p: any) => p[0] === seat.id));
        },
        placeholderCount(): number {
            if (!this.draft) return 0;
            return (JSON.stringify(this.draft.items || this.draft.sheet || []).match(/\[confirm:/g) || []).length;
        }
    },
    watch: {
        // Only processes can be recorded
        nt(now: string) {
            if (now != 'process' && this.inputMethod == 'record') this.inputMethod = 'describe';
        },
        // Create mode: the details panel appears with the first draft and keeps
        // the user's choices across revisions.
        phase(now: string) {
            if (now == 'review' && this.createMode && this.draft && !this.meta && !this.isExtend) {
                this.meta = metaFromDraft(this.draft, this.$store.state.user, this.fromSlug);
            }
        },
        'draft.name'(name: string) {
            if (this.meta && !this.slugTouched) this.meta.slug = slugify(name || '');
        }
    },
    created() {
        axios.get(`${this.$store.state.apiUrl}/api/ai/training`)
            .then(resp => { this.trainingCount = (resp.data.rules || []).length; })
            .catch(() => { /* indicator only */ });
        // The Forge page's "What do you want to make?" box hands its text over (AIHub.vue)
        if (!this.initialDraft && this.createMode && this.initialNote) this.description = this.initialNote;
        // …and its Record menu opens the record or upload card first
        if (!this.initialDraft && this.createMode && (this.initialMethod == 'upload' || (this.initialMethod == 'record' && this.recordable && this.canRecord))) this.inputMethod = this.initialMethod;
        if (this.initialDraft) {
            // Resume-review flow: open straight into the review phase
            this.draft = JSON.parse(JSON.stringify(this.initialDraft));
            this.description = this.initialNote || (this.editOnly ? 'Apply only the requested changes to the current editor draft.' : '(Process reconstructed from a screen recording)');
            this.phase = 'review';
            if (this.jobId) axios.get(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.jobId}`).then(async resp => {
                if (resp.data.notificationVersion && ['complete', 'error'].includes(resp.data.status)) {
                    this.displayedCompletion = { id: this.jobId, version: resp.data.notificationVersion };
                    await this.$nextTick();
                    this.acknowledgeVisibleCompletion();
                }
            }).catch(() => { /* Leave the fallback email enabled. */ });
            return;
        }
        if (this.mode == 'improve' && !this.videoImprove) {
            this.improve();
        }
    },
    mounted() {
        document.addEventListener('visibilitychange', this.acknowledgeVisibleCompletion);
        window.addEventListener('focus', this.acknowledgeVisibleCompletion);
    },
    unmounted() {
        this.componentGone = true;
        if (this.completionRetryTimer) clearTimeout(this.completionRetryTimer);
        document.removeEventListener('visibilitychange', this.acknowledgeVisibleCompletion);
        window.removeEventListener('focus', this.acknowledgeVisibleCompletion);
        if (this.loadingTimer) clearInterval(this.loadingTimer);
        if (this.pollTimer) clearInterval(this.pollTimer);
        if (this.screenRecUrl) URL.revokeObjectURL(this.screenRecUrl);
        this.photos.forEach((photo: any) => URL.revokeObjectURL(photo.url));
    },
    methods: {
        draftMediaUrl(value: string): string {
            const base = this.$store.state.apiUrl || '';
            if (!value || /^(https?:|data:|blob:|\/\/)/i.test(value) || (base && value.startsWith(base + '/'))) return value;
            return base + value;
        },
        async acknowledgeVisibleCompletion() {
            const result = this.displayedCompletion;
            if (!result || this.componentGone || this.phase === 'loading' || this.acknowledgingCompletion ||
                document.visibilityState !== 'visible' || !document.hasFocus()) return;
            const key = `${result.id}:${result.version}`;
            if (this.acknowledgedCompletion === key) return;
            if (this.completionAttemptKey !== key) { this.completionAttemptKey = key; this.completionAttempts = 0; }
            if (this.completionAttempts >= 3) return;
            if (this.completionRetryTimer) clearTimeout(this.completionRetryTimer);
            this.completionAttempts++;
            this.acknowledgingCompletion = true;
            try {
                await axios.post(`${this.$store.state.apiUrl}/api/ai/video-jobs/${result.id}/seen`, { version: result.version });
                this.acknowledgedCompletion = key;
            } catch {
                // Retry brief connection blips while the result remains visible; never suppress without a receipt.
                if (!this.componentGone && this.completionAttempts < 3) this.completionRetryTimer = setTimeout(this.acknowledgeVisibleCompletion, 1500);
            }
            finally { this.acknowledgingCompletion = false; }
        },
        async openDraftPreview() {
            if (!this.draft || this.previewLoading || this.busy) return;
            this.previewLoading = true;
            this.error = null;
            try {
                let base = this.previewBaseNode;
                const slug = this.draft.targetSlug || this.targetSlug || this.slug;
                if (!base && slug) base = (await axios.get(`${this.$store.state.apiUrl}/api/playbook/node/${slug}`)).data;
                base = JSON.parse(JSON.stringify(base || {}));
                const original = base.ProcessJSON ? JSON.parse(base.ProcessJSON) : {};
                const meta = this.meta;
                const ownerNames = meta ? (this.$store.state.users || []).filter((user: any) => (meta.processOwnerIDs || []).some((id: any) => String(id) === String(user.ID))).map((user: any) => `${user.FirstName} ${user.LastName}`).join(', ') : '';
                this.previewNode = {
                    ...base, Type: 'PROCESS', Name: this.draft.name, Slug: base.Slug || '',
                    ...(meta ? { LocationsJSON: JSON.stringify(meta.locations), DepartmentsJSON: JSON.stringify(meta.departments), TypesJSON: JSON.stringify(meta.types), ProcessOwnersJSON: JSON.stringify(meta.processOwnerIDs || []), ProcessOwnerNames: ownerNames } : {}),
                    ProcessJSON: JSON.stringify({ ...original, items: processDraftItems(this.draft.items, original.items).map(item => ({ ...item,
                        image: this.draftMediaUrl(item.image), image2: this.draftMediaUrl(item.image2), video: this.draftMediaUrl(item.video)
                    })) })
                };
                await this.$nextTick();
                (this.$refs.previewDialog as HTMLElement)?.focus();
            } catch (e: any) {
                this.error = 'Could not open the preview. Your draft is still here; please try again.';
            } finally { this.previewLoading = false; }
        },
        async closeDraftPreview() {
            this.previewNode = null;
            await this.$nextTick();
            (this.$refs.previewButton as HTMLElement)?.focus();
        },
        startLoading(kind: string) {
            this.error = null;
            this.phase = 'loading';
            const messages = LOADING_MESSAGES[`${kind}-${this.nt}`] || LOADING_MESSAGES[kind];
            let i = 0;
            this.loadingMessage = messages[0];
            if (this.loadingTimer) clearInterval(this.loadingTimer);
            this.loadingTimer = setInterval(() => {
                i = Math.min(i + 1, messages.length - 1);
                this.loadingMessage = messages[i];
            }, 6000);
        },
        stopLoading() {
            if (this.loadingTimer) clearInterval(this.loadingTimer);
            this.loadingTimer = null;
        },
        onVideoPicked(event: any) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;
            if (file.size > 300 * 1024 * 1024) {
                alert('Recordings up to 300 MB are supported');
                return;
            }
            this.discardScreenRec();
            this.clearFiles();
            this.videoFile = file;
        },
        // Photos and documents share one picker: images are resized client-side
        // as before, documents go up as-is (the server extracts them).
        async onFilesPicked(event: any) {
            const files = Array.from(event.target.files || []) as File[];
            event.target.value = '';
            if (!files.length) return;
            this.videoFile = null;
            this.discardScreenRec();
            const skipped: string[] = [];
            for (const file of files) {
                if (file.type.startsWith('image/')) {
                    if (this.photos.length >= 15) { alert('Up to 15 photos are supported'); continue; }
                    try {
                        const resized = await this.resizePhoto(file);
                        this.photos.push({ blob: resized, url: URL.createObjectURL(resized) });
                    } catch (e) {
                        console.log('photo skipped:', e);
                    }
                    continue;
                }
                const ext = (file.name.match(/\.[^.]+$/) || [''])[0].toLowerCase();
                if (!DOC_EXTENSIONS.includes(ext)) { skipped.push(file.name); continue; }
                if (file.size > 25 * 1024 * 1024) { alert(`"${file.name}" is over 25 MB — split it or export a smaller PDF`); continue; }
                if (this.docs.length >= MAX_DOCS) { alert(`Up to ${MAX_DOCS} documents per upload`); break; }
                if (this.docs.some((d: any) => d.file.name == file.name && d.file.size == file.size)) continue;
                this.docs.push({ file });
            }
            if (skipped.length) {
                alert(`Not supported: ${skipped.join(', ')}.\n\nUpload photos (JPG/PNG), PDF, Word (.docx) or text (.txt, .md, .csv) files. Save older .doc files as .docx or PDF first.`);
            }
        },
        removeDoc(i: number) {
            this.docs.splice(i, 1);
        },
        // Canvas re-encode: honors EXIF rotation (browsers orient decoded images)
        // and shrinks phone photos from ~5MB to a few hundred KB before upload.
        resizePhoto(file: File): Promise<Blob> {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.round(img.naturalWidth * scale);
                    canvas.height = Math.round(img.naturalHeight * scale);
                    const ctx2d = canvas.getContext('2d');
                    if (!ctx2d) { URL.revokeObjectURL(url); reject(new Error('no canvas')); return; }
                    ctx2d.drawImage(img, 0, 0, canvas.width, canvas.height);
                    URL.revokeObjectURL(url);
                    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('encode failed')), 'image/jpeg', 0.85);
                };
                img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable image')); };
                img.src = url;
            });
        },
        removePhoto(i: number) {
            URL.revokeObjectURL(this.photos[i].url);
            this.photos.splice(i, 1);
        },
        clearFiles() {
            this.photos.forEach((photo: any) => URL.revokeObjectURL(photo.url));
            this.photos = [];
            this.docs = [];
        },
        // Create mode: a recording builds any new type, but only adds to an existing process
        pickType(key: string) {
            if (this.isRecording) return;
            if (key == this.selectedType) return;
            if (key != 'process' && this.startFrom == 'existing' && (this.videoFile || this.screenRec)) {
                this.error = 'A recording can only add to an existing process. Your recording is still attached — choose "New" to build a new one from it, or remove it first.';
                return;
            }
            this.error = null;
            this.selectedType = key;
            this.clearTarget();
            if (this.startFrom == 'existing') this.loadNodes();
        },
        setStartFrom(mode: string) {
            if (mode == this.startFrom) return;
            if (mode == 'existing' && this.nt != 'process' && (this.videoFile || this.screenRec)) {
                this.error = `A recording can only add to an existing process. It can build a new ${this.typeNoun} — or remove it first to add to an existing one.`;
                return;
            }
            this.startFrom = mode;
            this.error = null;
            if (mode == 'existing') this.loadNodes(); else this.clearTarget();
        },
        async loadNodes() {
            this.loadingNodes = true;
            this.nodeOptions = [];
            await axios.get(`${this.$store.state.apiUrl}/api/ai/nodes`, { params: { type: this.nt } })
                .then(resp => { this.nodeOptions = resp.data || []; })
                .catch(() => { this.nodeOptions = []; });
            this.loadingNodes = false;
        },
        pickTarget(n: any) {
            this.targetSlug = n.slug;
            this.targetName = n.name;
            this.nodeSearch = '';
            this.error = null;
        },
        clearTarget() {
            this.targetSlug = '';
            this.targetName = '';
        },
        // "New" highlights in the review step (extend mode only)
        isNewItem(i: number): boolean {
            const added = this.draft && this.draft.added && this.draft.added.items;
            return Array.isArray(added) && added.includes(i);
        },
        isNewChecklistItem(item: any): boolean {
            const added = this.draft && this.draft.added && this.draft.added.ids;
            return Array.isArray(added) && !!item && added.includes(item.id);
        },
        seatLabel,
        isNewSeat(seat: any): boolean {
            const added = this.draft && this.draft.added && this.draft.added.seats;
            return Array.isArray(added) && added.includes(seat.id);
        },
        // sectionId: a section's id, or 'acc' for added accountabilities
        isChangedSection(seat: any, sectionId: string): boolean {
            const changed = this.draft && this.draft.added && this.draft.added.sections;
            return Array.isArray(changed) && changed.some((p: any) => p[0] === seat.id && p[1] === sectionId);
        },
        togglePick(list: string[], value: string) {
            const idx = list.indexOf(value);
            if (idx >= 0) list.splice(idx, 1); else list.push(value);
            this.detailsError = '';
        },
        onSlugEdit() {
            this.slugTouched = true;
            this.meta.slug = slugify(this.meta.slug || '');
        },
        onScreenRecorded(rec: any) {
            this.clearFiles();
            if (this.screenRecUrl) URL.revokeObjectURL(this.screenRecUrl);
            this.screenRec = rec;
            this.screenRecUrl = URL.createObjectURL(rec.blob);
            this.videoFile = null;
        },
        discardScreenRec() {
            if (this.screenRecUrl) { URL.revokeObjectURL(this.screenRecUrl); this.screenRecUrl = ''; }
            this.screenRec = null;
        },
        // The recording now belongs to the hand-off job; clear the capture so nothing is built here by accident
        onHandoffSent(info: any) {
            this.discardScreenRec();
            this.videoFile = null;
            this.description = '';
            this.error = null;
            this.handoffSent = info;
        },
        setMethod(method: string) {
            if (this.isRecording) return; // switching cards mid-recording would hide the recorder
            this.inputMethod = method;
        },
        async requestClose() {
            if (this.nt === 'meeting') { if (!(await (this.$refs.meetingRecorder as any)?.finishBeforeEnd())) return; this.$emit('close'); return; }
            if (this.isRecording && !confirm('A screen recording is in progress — closing will discard it. Close anyway?')) return;
            if (this.createMode && this.phase == 'review' && this.draft && !this.busy && !confirm(this.isExtend ? 'Close without saving these additions? They will be discarded.' : `Close without creating this ${this.typeLabel.toLowerCase()}? The draft will be discarded.`)) return;
            this.$emit('close');
        },
        async generate() {
            if (this.isRecording) return;
            if (this.videoImprove && !this.hasCapture) return;
            if (!this.description.trim() && !this.hasCapture) return;
            if (this.createMode && this.startFrom == 'existing' && !this.targetSlug) {
                this.error = `Pick the ${this.typeLabel.toLowerCase()} to add to first.`;
                return;
            }
            if (this.photos.length || this.docs.length) {
                this.startLoading(this.docs.length && !this.photos.length ? 'documents' : 'video');
                this.uploadPct = 0;
                const formData = new FormData();
                this.photos.forEach((photo: any, i: number) => formData.append('files', photo.blob, `photo_${String(i + 1).padStart(2, '0')}.jpg`));
                this.docs.forEach((doc: any) => formData.append('files', doc.file, doc.file.name));
                if (this.description.trim()) formData.append('note', this.description);
                formData.append('nodeType', this.nt);
                if (this.videoImprove && this.slug) { formData.append('slug', this.slug); formData.append('mode', 'update'); }
                else if (this.isExtend && this.targetSlug) { formData.append('slug', this.targetSlug); formData.append('mode', 'extend'); }
                await axios.post(`${this.$store.state.apiUrl}/api/ai/generate-from-files`, formData, {
                    timeout: 600000,
                    onUploadProgress: (e: any) => {
                        const pct = e.total ? Math.round((e.loaded / e.total) * 100) : null;
                        this.uploadPct = pct !== null && pct >= 100 ? null : pct;
                    }
                })
                    .then(resp => {
                        this.activeJobId = resp.data.jobId;
                        this.lastJobId = resp.data.jobId;
                        this.startJobPolling();
                    })
                    .catch(e => { this.uploadPct = null; this.stopLoading(); this.fail(e, 'input'); });
                this.uploadPct = null;
                return;
            }
            if (this.videoFile || this.screenRec) {
                this.startLoading('video');
                this.uploadPct = 0;
                const formData = new FormData();
                if (this.videoFile) {
                    formData.append('video', this.videoFile);
                } else {
                    const ext = (this.screenRec.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
                    formData.append('video', this.screenRec.blob, 'screen-recording.' + ext);
                    // MediaRecorder WebM has no duration header — the server uses this as a hint
                    formData.append('durationSec', String(Math.round(this.screenRec.durationSec)));
                }
                if (this.description.trim()) formData.append('note', this.description);
                if (this.videoImprove && this.slug) formData.append('slug', this.slug);
                formData.append('nodeType', this.nt);
                formData.append('saveVideo', this.saveVideo ? '1' : '0');
                await axios.post(`${this.$store.state.apiUrl}/api/ai/generate-from-video`, formData, {
                    timeout: 600000,
                    onUploadProgress: (e: any) => {
                        const pct = e.total ? Math.round((e.loaded / e.total) * 100) : null;
                        // hand off to the message carousel once the upload itself is done
                        this.uploadPct = pct !== null && pct >= 100 ? null : pct;
                    }
                })
                    .then(resp => {
                        // Upload accepted — generation continues server-side as a job.
                        this.activeJobId = resp.data.jobId;
                        this.lastJobId = resp.data.jobId;
                        this.startJobPolling();
                    })
                    .catch(e => { this.uploadPct = null; this.stopLoading(); this.fail(e, 'input'); });
                this.uploadPct = null;
                return;
            }
            if (this.isExtend) {
                this.startLoading('extend');
                await axios.post(`${this.$store.state.apiUrl}/api/ai/extend`, {
                    slug: this.targetSlug,
                    nodeType: this.nt,
                    description: this.description
                })
                    .then(resp => {
                        this.draft = resp.data;
                        this.revisionCount = 0;
                        this.phase = 'review';
                    })
                    .catch(e => this.fail(e, 'input'));
                this.stopLoading();
                return;
            }
            this.startLoading('generate');
            await axios.post(`${this.$store.state.apiUrl}/api/ai/generate`, {
                description: this.description,
                nodeType: this.nt
            })
                .then(resp => {
                    this.draft = resp.data;
                    this.revisionCount = 0;
                    this.phase = 'review';
                })
                .catch(e => this.fail(e, 'input'));
            this.stopLoading();
        },
        startJobPolling() {
            if (this.pollTimer) clearInterval(this.pollTimer);
            this.pollFailures = 0;
            this.pollTimer = setInterval(async () => {
                await axios.get(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.activeJobId}`)
                    .then(async resp => {
                        this.pollFailures = 0;
                        if (resp.data.stage) this.jobStage = resp.data.stage + '...';
                        if (resp.data.status === 'complete' && resp.data.draft) {
                            this.stopJobPolling();
                            this.stopLoading();
                            this.draft = resp.data.draft;
                            this.revisionCount = 0;
                            if (!this.description.trim()) {
                                const docNames = this.docs.map((d: any) => `"${d.file.name}"`).join(', ');
                                this.description = this.docs.length && this.photos.length
                                    ? `(Process reconstructed from photos of the work plus uploaded documents: ${docNames})`
                                    : this.docs.length
                                        ? `(Process converted from uploaded documents: ${docNames})`
                                        : this.photos.length
                                    ? '(Process reconstructed from photos of the work)'
                                    : this.videoFile
                                        ? `(Process reconstructed from an uploaded screen recording: "${this.videoFile.name}")`
                                        : '(Process reconstructed from a screen recording captured in the app)';
                            }
                            this.phase = 'review';
                        } else if (resp.data.status === 'error') {
                            this.stopJobPolling();
                            this.stopLoading();
                            this.error = resp.data.error || 'Could not process the recording — please try again.';
                            this.phase = 'input';
                        }
                        if (['complete', 'error'].includes(resp.data.status) && resp.data.notificationVersion) {
                            this.displayedCompletion = { id: resp.data.id, version: resp.data.notificationVersion };
                            await this.$nextTick();
                            this.acknowledgeVisibleCompletion();
                        }
                    })
                    .catch(() => {
                        // Network blips are fine; give up after three in a row
                        this.pollFailures++;
                        if (this.pollFailures >= 3) {
                            this.stopJobPolling();
                            this.stopLoading();
                            this.error = "Lost connection while generating — don't worry, we'll email you a link to the finished draft.";
                            this.phase = 'input';
                        }
                    });
            }, 2500);
        },
        stopJobPolling() {
            if (this.pollTimer) clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.activeJobId = null;
            this.jobStage = '';
        },
        moveStep(i: number, dir: number) {
            const j = i + dir;
            if (j < 0 || j >= this.draft.items.length) return;
            this.editingIdx = null;
            const items = this.draft.items;
            [items[i], items[j]] = [items[j], items[i]];
        },
        deleteStep(i: number) {
            if (!confirm('Delete this step?')) return;
            this.editingIdx = null;
            this.draft.items.splice(i, 1);
        },
        addStep() {
            this.draft.items.push({ header: 'New step', body: '<p></p>', image: '' });
            this.editingIdx = this.draft.items.length - 1;
        },
        async improve() {
            this.startLoading('improve');
            await axios.post(`${this.$store.state.apiUrl}/api/ai/improve`, {
                slug: this.slug,
                nodeType: this.nt,
                // role sheets are tuned up from what's on screen (AIImprovePanel passes it)
                ...(this.currentDraft ? { currentDraft: this.currentDraft } : {})
            })
                .then(resp => {
                    this.draft = resp.data;
                    this.revisionCount = 0;
                    this.phase = 'review';
                })
                .catch(e => this.fail(e, 'loading'));
            this.stopLoading();
        },
        async requestChanges() {
            if (!this.feedback.trim() || !this.draft || this.phase !== 'review' || this.busy) return;
            this.editingIdx = null;
            this.editingName = false;
            const endpoint = this.editOnly ? (this.nt == 'process' ? 'revise-process' : 'revise') : this.isExtend ? 'extend' : this.mode == 'generate' ? 'generate' : 'improve';
            const payload: any = {
                previousDraft: this.draft,
                feedback: this.feedback,
                nodeType: this.nt
            };
            if (this.mode == 'generate') payload.description = this.description;
            if (this.mode == 'improve') payload.slug = this.slug;
            if (this.isExtend) {
                payload.slug = this.draft.targetSlug || this.targetSlug;
                payload.description = this.description.trim() || 'Add what the attached files describe.';
            }

            this.startLoading('revise');
            await axios.post(`${this.$store.state.apiUrl}/api/ai/${endpoint}`, payload)
                .then(resp => {
                    const revised = resp.data;
                    // Revisions run through the text endpoint, which drops step
                    // screenshots — carry them over where headers still match.
                    const prevItems = (this.draft && this.draft.items) || [];
                    if (this.editOnly && this.nt == 'process' && revised.items) revised.items = processDraftItems(revised.items, prevItems);
                    if (this.nt == 'process' && revised.items && prevItems.some((p: any) => p.image)) {
                        const norm = (s: any) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
                        revised.items = revised.items.map((item: any) => {
                            if (item.image) return item;
                            const match = prevItems.find((p: any) => p.image && norm(p.header) && norm(p.header) == norm(item.header));
                            return { ...item, image: match ? match.image : '' };
                        });
                    }
                    this.draft = revised;
                    this.revisionCount++;
                    this.feedback = '';
                    this.phase = 'review';
                })
                .catch(e => this.fail(e, 'review'));
            this.stopLoading();
        },
        fail(e: any, backToPhase: string) {
            console.log(e);
            this.error = e?.response?.data || 'Something went wrong talking to the AI. Please try again.';
            this.phase = backToPhase == 'loading' ? 'input' : backToPhase;
            if (this.mode == 'improve' && !this.draft) this.phase = 'input';
        },
        renderBody(html: string): string {
            return (html || '').replace(/\[confirm:([^\]]*)\]/g, '<span class="ai-confirm-flag">[confirm:$1]</span>');
        },
        smallLines(item: any): string {
            return [item.smallLineOne, item.smallLineTwo, item.smallLineThree].filter(Boolean).join('  ·  ');
        },
        // For plain-text fields (checklist labels/descriptions): escape, then flag placeholders
        renderText(text: string): string {
            const escaped = (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            return escaped.replace(/\[confirm:([^\]]*)\]/g, '<span class="ai-confirm-flag">[confirm:$1]</span>');
        },
        retry() {
            this.error = null;
            if (this.editOnly && this.draft && this.feedback.trim()) {
                this.requestChanges();
            } else if (this.mode == 'improve' && !this.draft) {
                this.improve();
            } else if (this.phase == 'input') {
                this.generate();
            }
        },
        accept() {
            if (this.busy) return;
            this.editingIdx = null;
            this.editingName = false;
            if (this.isExtend) {
                this.$emit('accept', this.draft, this.jobId || this.lastJobId || this.draft.jobId || null, { nodeType: this.nt, extend: true, targetSlug: this.draft.targetSlug || this.targetSlug });
                return;
            }
            if (!this.createMode) {
                this.$emit('accept', this.draft, this.jobId || this.lastJobId || null, undefined, this.recordingBecomesVideo ? this.videoTitleValue.trim() : null);
                return;
            }
            // Create mode: validate the details the way the manual form does, then
            // hand the host everything it needs to create the node.
            const meta = this.meta || metaFromDraft(this.draft, this.$store.state.user, this.fromSlug);
            meta.name = String(this.draft.name || '').trim();
            if (!meta.slug) meta.slug = slugify(meta.name);
            this.detailsError = '';
            if (!meta.name) this.detailsError = `Give the ${this.typeLabel.toLowerCase()} a name — click the title above to edit it.`;
            else if (!meta.locations.length) this.detailsError = 'Pick at least one location.';
            else if (!meta.departments.length) this.detailsError = 'Pick at least one department.';
            else if (!(meta.folderSlugs || []).length) this.detailsError = 'Pick at least one folder so people can find it.';
            if (this.detailsError) return;
            this.$emit('accept', this.draft, this.jobId || this.lastJobId || null, { ...meta, nodeType: this.nt, videoTitle: this.recordingBecomesVideo ? this.videoTitleValue.trim() : undefined, locations: meta.locations.slice(), departments: meta.departments.slice(), types: meta.types.slice(), folderSlugs: (meta.folderSlugs || []).slice() });
        }
    }
});
</script>

<style scoped>
.ai-modal {
    width: 760px;
    border: none !important;
    border-radius: 18px;
    box-shadow: 0 24px 70px rgba(70, 35, 10, .45), 0 0 0 1px rgba(249, 115, 22, .15);
    /* overflow-y (not hidden): the host wrapper caps modals at calc(100vh - 120px),
       so the modal must scroll when content is taller than the window — otherwise
       the bottom action buttons are clipped and unreachable. */
    overflow-y: auto;
    overflow-x: hidden;
}
.ai-modal::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: var(--ai-grad);
}
.ai-modal-header { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
.ai-modal-title { font-size: 21px; font-weight: 700; color: #1f1712; display: flex; align-items: center; gap: 8px; }
.ai-modal-subtitle { color: #8a7f75; font-size: 13.5px; margin-top: 2px; }
.ai-trained-pill {
    display: inline-flex; align-items: center; gap: 4px; margin-left: 8px;
    font-size: 11px; font-weight: 700; color: var(--ai-accent);
    background: #fff4ea; border: 1px solid var(--ai-border-soft); border-radius: 12px; padding: 1px 9px;
    vertical-align: middle;
}
.ai-muted { color: #6f655c; }
.ai-modal textarea {
    border: 1px solid var(--ai-border-soft);
    border-radius: 10px;
    padding: 12px;
    font-size: 14px;
    transition: border-color .2s, box-shadow .2s;
}
.ai-modal textarea:focus {
    outline: none;
    border-color: #fdba74;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, .15);
}
.ai-loading { text-align: center; padding: 46px 0; }
.ai-loading-orb {
    width: 46px; height: 46px; border-radius: 50%; margin: 0 auto 18px;
    background: conic-gradient(from 0deg, #f59e0b, #f97316, #ea580c, #f59e0b);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 6px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 6px));
    animation: ai-spin 1.1s linear infinite;
}
@keyframes ai-spin { to { transform: rotate(360deg); } }
.ai-loading-msg { font-weight: 600; margin-bottom: 6px; min-height: 22px; color: #4a3728; }
.ai-error { background: #fdecea; border: 1px solid #f5c6c3; color: #9c2b23; padding: 10px 14px; border-radius: 10px; margin: 12px 0; }
.ai-handoff-done { background: #eafaf0; border: 1px solid #b7e4c7; color: #1b5e37; padding: 10px 14px; border-radius: 10px; margin: 0 0 14px; font-size: 13.5px; line-height: 1.5; }
.ai-handoff-done a { margin-left: 8px; cursor: pointer; text-decoration: underline; color: #1b7a3d; }
.ai-error a { color: #9c2b23; text-decoration: underline; cursor: pointer; }
.ai-draft-name { font-size: 22px; font-weight: 700; margin-bottom: 12px; color: #1f1712; }
.ai-section-label { font-weight: 700; margin: 16px 0 6px; text-transform: uppercase; font-size: 11.5px; letter-spacing: .08em; color: #a0958a; }
.ai-changes { background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 12px; padding: 10px 16px; }
.ai-changes ul { margin: 6px 0; padding-left: 20px; }
.ai-changes li { margin-bottom: 4px; }
.ai-preview { border: 1px solid #f6e3d3; border-radius: 12px; max-height: min(58vh, 900px); overflow: auto; padding: 4px 16px; background: #fffcf9; box-shadow: inset 0 2px 6px rgba(124, 58, 18, .04); }
.ai-preview-item { padding: 12px 0; border-bottom: 1px solid #f9ece0; }
.ai-preview-item:last-child { border-bottom: none; }
.ai-preview-header { font-weight: 700; margin-bottom: 6px; color: #2b221b; }
.ai-preview-body :deep(p) { margin: 6px 0; }
.ai-preview-body :deep(ul), .ai-preview-body :deep(ol) { margin: 6px 0; padding-left: 22px; }
.ai-preview-textblock { padding: 14px 0 4px; color: #2b221b; border-bottom: 1px solid #f9ece0; }
.ai-preview-textblock :deep(p) { margin: 6px 0; }
.ai-preview-task { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #fbf1e8; }
.ai-preview-task:last-child { border-bottom: none; }
.ai-task-box { width: 15px; height: 15px; min-width: 15px; margin-top: 2px; border: 2px solid #e8c9ae; border-radius: 4px; background: white; }
.ai-task-text { font-size: 14px; color: #2b221b; line-height: 1.45; }
.ai-task-desc { color: #6f655c; }
.ai-preview-seats { display: flex; flex-direction: column; gap: 12px; }
.ai-seat { border: 1px solid #f0dfcf; border-radius: 10px; padding: 12px 14px; background: white; }
.ai-seat-who { font-weight: 700; font-size: 15px; color: #2b221b; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
.ai-seat-tag { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: var(--ai-accent-deep); background: var(--ai-grad-soft); border-radius: 4px; padding: 2px 6px; }
.ai-seat-sec { padding: 6px 8px; border-radius: 6px; font-size: 12.5px; color: #2b221b; }
.ai-seat-h { font-weight: 700; font-size: 12px; color: var(--ai-accent-deep); margin-bottom: 2px; }
.ai-seat-sec :deep(p) { margin: 4px 0; }
.ai-seat-sec :deep(ul), .ai-seat-sec :deep(ol), .ai-seat-sec ol { margin: 4px 0; padding-left: 18px; }
.ai-preview-tl { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid #fbf1e8; }
.ai-preview-tl:last-child { border-bottom: none; }
.ai-tl-rail { display: flex; flex-direction: column; align-items: center; padding-top: 3px; }
.ai-tl-dot { width: 14px; height: 14px; min-width: 14px; border-radius: 50%; border: 2px solid rgba(0,0,0,.08); }
.ai-tl-body { font-size: 14px; color: #2b221b; line-height: 1.45; }
.ai-tl-line1 { font-weight: 700; }
.ai-tl-line2 { color: #5c4636; font-size: 13px; }
.ai-tl-small { color: #8a7f75; font-size: 12px; margin-top: 2px; }
.ai-tl-desc { color: #5c4636; font-size: 13px; margin-top: 4px; }
.ai-tl-desc :deep(p) { margin: 4px 0; }
.ai-tl-desc :deep(ul), .ai-tl-desc :deep(ol) { margin: 4px 0; padding-left: 18px; }
.ai-chip { display: inline-block; background: var(--ai-grad); color: white; border-radius: 20px; padding: 3px 13px; font-size: 12px; font-weight: 600; margin: 0 6px 6px 0; box-shadow: 0 2px 6px rgba(234, 88, 12, .25); }
.ai-chip-type { background: white; color: var(--ai-accent); border: 1px solid var(--ai-border-soft); box-shadow: none; }
.ai-actions { display: flex; flex-wrap: wrap; justify-content: space-between; margin-top: 16px; gap: 10px; }
.ai-accept:hover, .ai-accept:focus-visible { background: var(--ai-grad-hover); color: white !important; border-color: var(--ai-accent); }
.ai-draft-preview { z-index: 10000000; overflow: auto; }
.ai-draft-preview :deep(.page-wrap) { min-height: 100%; }
.ai-placeholder-warn {
    background: #fff8e6; border: 1px solid #ffe08a; color: #8a6d00; border-radius: 10px;
    padding: 8px 14px; margin-top: 12px; font-size: 13.5px;
}
:deep(.ai-confirm-flag) { background: #fff3cd; border: 1px solid #ffe08a; color: #8a6d00; border-radius: 4px; padding: 0 4px; font-size: 12px; }
.ai-textarea-wrap { position: relative; }
.ai-textarea-wrap textarea { padding-right: 44px; }
.ai-dict-pos { position: absolute; right: 10px; bottom: 12px; }
.ai-video-row { display: flex; align-items: center; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
.ai-preview-shot { max-width: 100%; border: 1px solid #f6e3d3; border-radius: 8px; margin-top: 8px; display: block; }
.ai-upload-bar {
    width: 260px; height: 6px; margin: 0 auto 12px; border-radius: 3px;
    background: #fbeadb; overflow: hidden;
}
.ai-upload-bar span { display: block; height: 100%; background: var(--ai-grad); border-radius: 3px; transition: width .3s ease; }
.ai-rec-preview { width: 100%; }
.ai-rec-preview video {
    width: 100%; max-height: 260px; border-radius: 12px; background: #000;
    border: 1px solid var(--ai-border-soft);
}
.ai-rec-preview-row { display: flex; align-items: center; gap: 14px; margin-top: 6px; font-size: 13px; }
.ai-rec-caption { color: #2b221b; font-weight: 600; }
.ai-rec-preview-row a { color: var(--ai-accent); cursor: pointer; text-decoration: underline; }
.ai-save-video { position: relative; display: flex; align-items: flex-start; gap: 10px; margin: 10px 0 4px; cursor: pointer; font-size: 13.5px; color: #2b221b; line-height: 1.4; }
.ai-save-video input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.ai-save-video-switch { flex: none; position: relative; width: 34px; height: 20px; border-radius: 10px; background: #d8cfc6; transition: background .15s ease; margin-top: 1px; }
.ai-save-video-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform .15s ease; }
.ai-save-video input:checked + .ai-save-video-switch { background: var(--ai-accent); }
.ai-save-video input:checked + .ai-save-video-switch::after { transform: translateX(14px); }
.ai-save-video input:focus-visible + .ai-save-video-switch { outline: 2px solid var(--ai-accent); outline-offset: 2px; }
.ai-save-video em { display: block; font-style: normal; font-size: 12.5px; color: #6f655c; }
.ai-edit-btn {
    display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
    width: 22px; height: 22px; border-radius: 6px; color: #a0958a; margin-left: 6px;
    vertical-align: middle; transition: all .15s ease;
}
.ai-edit-btn:hover { color: var(--ai-accent); background: #fff4ea; }
.ai-draft-name { cursor: pointer; }
.ai-edit-name {
    font-size: 22px; font-weight: 700; color: #1f1712; width: 100%; box-sizing: border-box;
    border: 1px solid var(--ai-border-soft); border-radius: 8px; padding: 6px 10px; margin-bottom: 12px;
}
.ai-edit-header {
    font-weight: 700; color: #2b221b; width: 100%; box-sizing: border-box;
    border: 1px solid var(--ai-border-soft); border-radius: 8px; padding: 6px 10px; margin-bottom: 8px;
}
.ai-edit-done { padding: 4px 14px; font-size: 13px; }
.ai-preview-item :deep(.ProseMirror) { min-height: 60px; }
.ai-step-tools { white-space: nowrap; }
.ai-tool-off { opacity: .25; pointer-events: none; }
.ai-tool-danger:hover { color: #d22630; background: #fdecea; }
.ai-add-step { color: var(--ai-accent); cursor: pointer; text-decoration: underline; font-size: 13.5px; }
.ai-preview-shot { cursor: zoom-in; }
.ai-lightbox {
    position: fixed; inset: 0; z-index: 9999; background: rgba(30, 20, 12, .88);
    display: flex; align-items: center; justify-content: center; cursor: zoom-out; padding: 30px;
}
.ai-lightbox img { max-width: 95vw; max-height: 92vh; border-radius: 10px; box-shadow: 0 20px 80px rgba(0,0,0,.6); }
.ai-photo-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.ai-photo-thumb { position: relative; }
.ai-photo-thumb img { width: 74px; height: 56px; object-fit: cover; border-radius: 8px; border: 1px solid var(--ai-border-soft); display: block; }
.ai-photo-x {
    position: absolute; top: -6px; right: -6px; width: 18px; height: 18px; border-radius: 50%;
    background: #2b221b; color: #fff; font-size: 12px; line-height: 18px; text-align: center; cursor: pointer;
}
.ai-video-chip {
    display: inline-flex; align-items: center; font-size: 13px; color: #2b221b;
    background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 20px; padding: 4px 12px;
}
/* Document chips beside the photo thumbnails */
.ai-doc-chip {
    display: inline-flex; align-items: center; gap: 6px; max-width: 260px; font-size: 13px; color: #2b221b;
    background: #fff; border: 1px solid var(--ai-border-soft); border-radius: 10px; padding: 6px 10px;
    box-shadow: 0 2px 8px rgba(234, 88, 12, .08);
}
.ai-doc-chip svg { color: var(--ai-accent); flex-shrink: 0; }
.ai-doc-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-doc-chip em { font-style: normal; color: #8a7f75; font-size: 11.5px; white-space: nowrap; }
.ai-doc-chip a { color: #8a7f75; cursor: pointer; font-size: 16px; line-height: 1; margin-left: 2px; text-decoration: none; }
.ai-doc-chip a:hover { color: #d22630; }
/* Create mode: type picker */
.ai-type-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
.ai-type-label { font-size: 13px; font-weight: 600; color: #8a7f75; margin-right: 2px; }
.ai-type-pill {
    font-size: 13px; font-weight: 600; color: #5c4636; background: #fff; border: 1px solid var(--ai-border-soft);
    border-radius: 20px; padding: 6px 14px; cursor: pointer; transition: all .15s ease; user-select: none;
}
.ai-type-pill:hover { background: #fffaf5; border-color: #fdba74; }
.ai-type-on, .ai-type-on:hover { background: var(--ai-grad); color: #fff; border-color: transparent; box-shadow: 0 2px 8px rgba(234, 88, 12, .28); }
/* Inline on The Forge page: same card, no overlay */
.ai-modal-inline {
    width: auto; max-width: 100%; margin: 0; padding: 26px 28px 24px;
    /* The top bar is part of the background here (not ::before), so the rounded corners clip it: this card stays
       overflow: visible so nothing inside it gets cut off */
    background: var(--ai-grad) top / 100% 4px no-repeat, linear-gradient(180deg, #fff8f1 0, #fff 150px);
    box-shadow: 0 2px 14px rgba(124, 58, 18, .08), 0 0 0 1px #f6e3d3;
    overflow: visible;
}
.ai-modal-inline::before { display: none; }
/* Segmented pickers (Make a / Start from) */
.ai-seg { display: inline-flex; flex-wrap: wrap; gap: 2px; padding: 3px; background: #f5f0eb; border: 1px solid #ece3da; border-radius: 10px; }
.ai-seg .ai-type-pill {
    display: inline-flex; align-items: center; gap: 6px; border: none; border-radius: 8px; padding: 6px 12px;
    background: transparent; color: #6f655c; box-shadow: none;
}
.ai-seg .ai-type-pill:hover { background: rgba(255, 255, 255, .6); color: #2b221b; }
.ai-seg .ai-type-on, .ai-seg .ai-type-on:hover { background: #fff; color: #1f1712; box-shadow: 0 1px 4px rgba(60, 30, 10, .14); }
.ai-seg .ai-type-pill svg { width: 14px; height: 14px; flex-shrink: 0; }
.ai-seg .ai-type-pill svg:not([fill="none"]) { fill: currentColor; }
.ai-type-row .ai-type-label, .ai-start-row .ai-type-label { min-width: 74px; }
/* Method cards: Describe it / Record your screen / Upload */
.ai-methods { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px; margin: 4px 0 12px; }
.ai-method {
    display: flex; flex-direction: column; align-items: flex-start; gap: 3px; text-align: left; cursor: pointer;
    background: #fff; border: 1px solid #ece3da; border-radius: 10px; padding: 12px 14px; font: inherit; color: #2b221b;
    transition: border-color .15s, box-shadow .15s, background .15s;
}
.ai-method:hover { border-color: #fdba74; }
.ai-method svg { width: 18px; height: 18px; color: var(--ai-accent); margin-bottom: 4px; }
.ai-method strong { font-size: 14px; }
.ai-method span { font-size: 12.5px; color: #8a7f75; }
.ai-method-on, .ai-method-on:hover { border-color: #f59e5b; background: #fffaf5; box-shadow: 0 0 0 3px rgba(249, 115, 22, .12); }
.ai-methods-locked .ai-method:not(.ai-method-on) { opacity: .5; cursor: not-allowed; }
.ai-input-stack { display: flex; flex-direction: column; }
.ai-capture-first .ai-textarea-wrap { order: 2; margin-top: 10px; }
.ai-capture-first .ai-video-row:empty { display: none; }
/* Create mode: start-from row + existing-node picker */
.ai-start-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: -4px 0 14px; }
.ai-target { margin: -4px 0 14px; }
.ai-target-search {
    width: 100%; box-sizing: border-box; border: 1px solid var(--ai-border-soft); border-radius: 10px 10px 0 0;
    padding: 9px 12px; font-size: 14px; margin: 0;
}
.ai-target-search:focus { outline: none; border-color: #fdba74; box-shadow: 0 0 0 3px rgba(249, 115, 22, .15); }
.ai-target-list { border: 1px solid var(--ai-border-soft); border-top: none; border-radius: 0 0 10px 10px; max-height: 200px; overflow: auto; background: #fff; }
.ai-target-option { padding: 8px 12px; font-size: 14px; color: #2b221b; cursor: pointer; border-bottom: 1px solid #fbf1e8; }
.ai-target-option:last-child { border-bottom: none; }
.ai-target-option:hover { background: #fff4ea; color: var(--ai-accent-deep); }
.ai-target-draft { font-size: 11px; color: #a0958a; margin-left: 6px; }
.ai-target-chip {
    display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: #2b221b;
    background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 10px; padding: 8px 14px;
}
.ai-target-chip svg { color: var(--ai-accent); }
.ai-target-chip a { color: var(--ai-accent-deep); cursor: pointer; text-decoration: underline; font-size: 13px; margin-left: 4px; }
.ai-extend-lead { font-size: 13px; font-weight: 600; color: #a0958a; text-transform: uppercase; letter-spacing: .06em; margin-right: 8px; }
/* "New" highlights in the extend review */
.ai-new-tag {
    display: inline-block; background: var(--ai-grad); color: #fff; font-size: 10px; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase; border-radius: 6px; padding: 1px 7px; margin-left: 8px; vertical-align: middle;
}
.ai-preview-item.ai-new, .ai-preview-tl.ai-new { background: #fff4ea; margin: 0 -16px; padding-left: 16px; padding-right: 16px; border-left: 3px solid var(--ai-accent); }
.ai-preview .ai-new > .ai-preview-task, .ai-preview .ai-new > .ai-preview-textblock { background: #fff4ea; margin: 0 -16px; padding-left: 13px; padding-right: 16px; border-left: 3px solid var(--ai-accent); }
.ai-seat-sec.ai-new-cell { background: #fff4ea; box-shadow: inset 0 0 0 2px var(--ai-accent); }
/* Create mode: details panel in the review step */
.ai-details { margin-top: 16px; background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 12px; padding: 14px 16px; }
.ai-details-hint { text-transform: none; letter-spacing: 0; font-weight: 500; color: #8a7f75; margin-left: 6px; font-size: 12px; }
.ai-video-note { margin: 12px 0 0; padding: 9px 12px; border-radius: 10px; background: #fff7ed; border: 1px solid var(--ai-border-soft, #fde4cf); color: #6b5d52; font-size: 13px; }
.ai-video-note strong { color: var(--ai-accent-deep, #c2410c); }
.ai-video-note label { display: block; margin-bottom: 6px; }
.ai-video-note input { display: block; width: 100%; box-sizing: border-box; margin-bottom: 6px; padding: 8px 10px; border: 1px solid var(--ai-border-soft, #fde4cf); border-radius: 8px; background: #fff; font: inherit; font-size: 14px; color: #2b2520; }
.ai-video-note input:focus { outline: 2px solid var(--ai-accent, #ea580c); outline-offset: 1px; }
.ai-details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; }
.ai-details-field label { display: block; font-size: 12px; font-weight: 700; color: #5c4636; margin-bottom: 6px; }
.ai-details-wide { grid-column: 1 / -1; }
.ai-details-field input[type=text], .ai-details-field select {
    border: 1px solid var(--ai-border-soft); border-radius: 8px; padding: 7px 10px; font-size: 13.5px; background: #fff; color: #1f1712; box-sizing: border-box; margin: 0;
}
.ai-details-field input[type=text]:focus, .ai-details-field select:focus { outline: none; border-color: #fdba74; box-shadow: 0 0 0 3px rgba(249, 115, 22, .15); }
.ai-pills { display: flex; flex-wrap: wrap; gap: 6px; max-height: 118px; overflow: auto; }
.ai-pill {
    font-size: 12.5px; font-weight: 600; color: #5c4636; background: #fff; border: 1px solid var(--ai-border-soft);
    border-radius: 20px; padding: 4px 12px; cursor: pointer; transition: all .15s ease; user-select: none;
}
.ai-pill:hover { border-color: #fdba74; background: #fffaf5; }
.ai-pill-on, .ai-pill-on:hover { background: var(--ai-grad); color: #fff; border-color: transparent; box-shadow: 0 2px 6px rgba(234, 88, 12, .25); }
.ai-pill-alt.ai-pill-on { background: #fff; color: var(--ai-accent); border-color: var(--ai-accent); box-shadow: none; }
.ai-field-warn { color: #a16207; font-size: 12px; margin-top: 5px; }
.ai-slug-row { display: flex; align-items: center; gap: 4px; }
.ai-slug-row span { color: #8a7f75; font-size: 13px; white-space: nowrap; }
.ai-slug-row input { flex: 1; min-width: 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px; }
@media (max-width: 640px) { .ai-details-grid { grid-template-columns: 1fr; } }
</style>
